import { ApiService } from "./api.service";
import type {
  ChartResponse,
  Dashboard,
  DashboardCreateBody,
  DashboardPreviewsResponse,
  DashboardShare,
  DashboardShareCreateBody,
  DashboardShareUpdateBody,
  DashboardUpdateBody,
  DashboardWidget,
  UserCapacityBreakdown,
  WidgetCreateBody,
  WidgetMetadataResponse,
  WidgetSourceMetadata,
  WidgetUpdateBody,
} from "@/types/dashboard";

/** Ids one previews call may carry. Mirrors `MAX_DASHBOARDS` in
 *  `apps/backend/cognota/dashboards/views/preview.py`, which 400s past it: the
 *  cap bounds the work one request can ask for (that many dashboards times three
 *  widgets), so it is raised on the server or not at all. */
export const DASHBOARD_PREVIEW_BATCH = 24;

export class DashboardService extends ApiService {
  async list(slug: string): Promise<Dashboard[]> {
    return this.get(`/api/workspaces/${slug}/dashboards/`);
  }

  /** Batched chart previews for a page of list cards.
   *
   * At most {@link DASHBOARD_PREVIEW_BATCH} ids per call — the endpoint rejects
   * a longer list rather than truncating it, so callers must batch.
   *
   * One call for every card on screen rather than one per widget: the
   * per-request overhead is what dominates a preview, not the aggregate behind
   * it. Kept off `list()` on purpose so the list's own latency is untouched and
   * a preview failure cannot break it.
   */
  async listPreviews(slug: string, dashboardIds: string[]): Promise<DashboardPreviewsResponse> {
    const params = new URLSearchParams({ ids: dashboardIds.join(",") });
    return this.get(`/api/workspaces/${slug}/dashboards/previews/`, { params });
  }

  async retrieve(slug: string, dashboardId: string): Promise<Dashboard> {
    return this.get(`/api/workspaces/${slug}/dashboards/${dashboardId}/`);
  }

  async listWidgets(slug: string, dashboardId: string): Promise<DashboardWidget[]> {
    return this.get(`/api/workspaces/${slug}/dashboards/${dashboardId}/widgets/`);
  }

  async getChart(
    slug: string,
    dashboardId: string,
    widgetId: string,
    filters?: URLSearchParams,
  ): Promise<ChartResponse> {
    const url = `/api/workspaces/${slug}/dashboards/${dashboardId}/widgets/${widgetId}/charts/`;
    return this.get(url, { params: filters });
  }

  async create(slug: string, body: DashboardCreateBody): Promise<Dashboard> {
    return this.post(`/api/workspaces/${slug}/dashboards/`, body);
  }

  async deleteDashboard(slug: string, dashboardId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/dashboards/${dashboardId}/`);
  }

  /** Deep-copy a dashboard the caller can see into a private board they own
   *  (NG-1260). No body: the copy semantics are fixed server-side. */
  async duplicate(slug: string, dashboardId: string): Promise<Dashboard> {
    return this.post(`/api/workspaces/${slug}/dashboards/${dashboardId}/duplicate/`);
  }

  async update(slug: string, dashboardId: string, body: DashboardUpdateBody): Promise<Dashboard> {
    return this.patch(`/api/workspaces/${slug}/dashboards/${dashboardId}/`, body);
  }

  async createWidget(
    slug: string,
    dashboardId: string,
    body: WidgetCreateBody,
  ): Promise<DashboardWidget> {
    return this.post(`/api/workspaces/${slug}/dashboards/${dashboardId}/widgets/`, body);
  }

  async updateWidget(
    slug: string,
    dashboardId: string,
    placementId: string,
    body: WidgetUpdateBody,
  ): Promise<DashboardWidget> {
    // The widget PATCH endpoint reads placement fields (filters/coords/size) at
    // the top level but chart-config fields ONLY from a nested `widget` key
    // (unlike create, which accepts flat fields). Split the flat body so
    // config changes — chart type, axes, metric, name — actually persist.
    const PLACEMENT_KEYS = new Set(["filters", "x_axis_coord", "y_axis_coord", "width", "height"]);
    const payload: Record<string, unknown> = {};
    const widget: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(body)) {
      if (PLACEMENT_KEYS.has(key)) {
        payload[key] = value;
      } else {
        widget[key] = value;
      }
    }
    if (Object.keys(widget).length > 0) payload.widget = widget;
    return this.patch(
      `/api/workspaces/${slug}/dashboards/${dashboardId}/widgets/${placementId}/`,
      payload,
    );
  }

  async deleteWidget(slug: string, dashboardId: string, placementId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/dashboards/${dashboardId}/widgets/${placementId}/`);
  }

  // Data layer for #1558 (drag/resize grid UI to follow).
  async bulkUpdateWidgets(
    slug: string,
    dashboardId: string,
    widgets: {
      id: string;
      x_axis_coord?: number;
      y_axis_coord?: number;
      width?: number;
      height?: number;
    }[],
  ): Promise<DashboardWidget[]> {
    return this.patch(`/api/workspaces/${slug}/dashboards/${dashboardId}/bulk-update-widgets/`, {
      widgets,
    });
  }

  /** #1972 — per-source builder capabilities (keys only). */
  async getWidgetMetadata(slug: string): Promise<WidgetSourceMetadata[]> {
    const url = `/api/workspaces/${slug}/dashboards/widget-metadata/`;
    const resp = await this.get<WidgetMetadataResponse>(url);
    return resp.sources;
  }

  // --- Sharing (NG-1115) ---------------------------------------------------
  // Managing shares is an ownership right on the backend (`ensure_dashboard_owner`);
  // these calls 403 for a non-owner. The list is what the Share modal renders.
  async listShares(slug: string, dashboardId: string): Promise<DashboardShare[]> {
    return this.get(`/api/workspaces/${slug}/dashboards/${dashboardId}/shares/`);
  }

  async createShare(
    slug: string,
    dashboardId: string,
    body: DashboardShareCreateBody,
  ): Promise<DashboardShare> {
    return this.post(`/api/workspaces/${slug}/dashboards/${dashboardId}/shares/`, body);
  }

  async updateShare(
    slug: string,
    dashboardId: string,
    shareId: string,
    body: DashboardShareUpdateBody,
  ): Promise<DashboardShare> {
    return this.patch(`/api/workspaces/${slug}/dashboards/${dashboardId}/shares/${shareId}/`, body);
  }

  async deleteShare(slug: string, dashboardId: string, shareId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/dashboards/${dashboardId}/shares/${shareId}/`);
  }

  // Capacity PR3 — W4 drill-down. Fetches per-user hours breakdown for a
  // single bar in the W3 team-utilization chart.
  async getUserCapacityBreakdown(
    slug: string,
    dashboardId: string,
    userId: string,
    filters?: URLSearchParams,
  ): Promise<UserCapacityBreakdown> {
    const url = `/api/workspaces/${slug}/dashboards/${dashboardId}/user-capacity/${userId}/breakdown/`;
    return this.get(url, { params: filters });
  }
}
